import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SeoTags } from "../../components/Head/SeoTags";

const BlogPage = () => {
  const { t } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const loadPosts = async () => {
      try {
        const response = await fetch("/blog/posts.json");
        const data = await response.json();
        setPosts(data.posts);
        setCategories(data.categories || []);
      } catch (error) {
        console.error("Failed to load blog posts:", error);
      } finally {
        setLoading(false);
      }
    };

    loadPosts();
  }, []);

  const filteredPosts = selectedCategory === "all"
    ? posts
    : posts.filter(post => post.category === selectedCategory);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  };

  return (
    <>
      <SeoTags
        title="Blog - Bookmark Tips, Productivity & Updates | onBookmarks"
        description="Discover bookmark organization tips, productivity guides, and the latest updates from onBookmarks. Learn how to better organize your digital life."
        keywords="bookmark tips, productivity blog, bookmark organization, digital organization, onbookmarks updates"
        canonical="https://onbookmarks.com/blog/"
        localized={false}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            name: "onBookmarks Blog",
            description: "Bookmark tips, productivity guides, and updates",
            url: "https://onbookmarks.com/blog",
            publisher: {
              "@type": "Organization",
              name: "onBookmarks",
              logo: {
                "@type": "ImageObject",
                url: "https://onbookmarks.com/images/logo.png"
              }
            }
          }),
        }}
      />

      {/* Hero Section */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto text-center">
              <h1 className="display-4 fw-bold mb-4">onBookmarks Blog</h1>
              <p className="lead text-muted">
                Tips, guides, and updates to help you organize your digital world
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Content */}
      <section className="py-5">
        <div className="container">
          <div className="row">
            {/* Main Content */}
            <div className="col-lg-8">
              {/* Category Filter */}
              <div className="mb-4">
                <div className="d-flex flex-wrap gap-2">
                  <button
                    className={`btn btn-sm ${selectedCategory === "all" ? "btn-purple" : "btn-outline-secondary"}`}
                    onClick={() => setSelectedCategory("all")}
                  >
                    All Posts
                  </button>
                  {categories.map((category) => (
                    <button
                      key={category}
                      className={`btn btn-sm ${selectedCategory === category ? "btn-purple" : "btn-outline-secondary"}`}
                      onClick={() => setSelectedCategory(category)}
                    >
                      {category.charAt(0).toUpperCase() + category.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Posts List */}
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-purple" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : filteredPosts.length === 0 ? (
                <div className="text-center py-5">
                  <i className="bi bi-journal-text display-1 text-muted mb-3"></i>
                  <p className="text-muted">No posts found in this category.</p>
                </div>
              ) : (
                <div className="row g-4">
                  {filteredPosts.map((post) => (
                    <div key={post.slug} className="col-12">
                      <article className="card border-0 shadow-sm h-100">
                        <div className="card-body p-4">
                          <div className="d-flex align-items-center gap-2 mb-3">
                            <span className="badge bg-purple-subtle text-purple">
                              {post.category}
                            </span>
                            <span className="text-muted small">
                              {formatDate(post.date)}
                            </span>
                          </div>
                          <h2 className="h4 mb-3">
                            <Link
                              to={`/blog/${post.slug}`}
                              className="text-dark text-decoration-none hover-text-purple"
                            >
                              {post.title}
                            </Link>
                          </h2>
                          <p className="text-muted mb-3">{post.excerpt}</p>
                          <div className="d-flex align-items-center justify-content-between">
                            <span className="text-muted small">
                              <i className="bi bi-person me-1"></i>
                              {post.author}
                            </span>
                            <Link
                              to={`/blog/${post.slug}`}
                              className="btn btn-link text-purple p-0"
                            >
                              Read more <i className="bi bi-arrow-right"></i>
                            </Link>
                          </div>
                        </div>
                      </article>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="col-lg-4">
              <div className="sticky-top" style={{ top: "100px" }}>
                {/* Newsletter CTA */}
                <div className="card border-0 bg-purple mb-4">
                  <div className="card-body p-4 text-center">
                    <i className="bi bi-envelope-heart display-4 mb-3 text-white"></i>
                    <h3 className="h5 mb-3 text-white">Stay Updated</h3>
                    <p className="small mb-3 text-white">
                      Get the latest tips and updates delivered to your inbox.
                    </p>
                    <Link
                      to={`${process.env.REACT_APP_APP_URL}/register`}
                      className="btn btn-light btn-sm"
                    >
                      Join onBookmarks
                    </Link>
                  </div>
                </div>

                {/* Quick Links */}
                <div className="card border-0 shadow-sm">
                  <div className="card-body p-4">
                    <h3 className="h6 mb-3">Quick Links</h3>
                    <ul className="list-unstyled mb-0">
                      <li className="mb-2">
                        <Link to="/features" className="text-muted text-decoration-none">
                          <i className="bi bi-arrow-right me-2"></i>All Features
                        </Link>
                      </li>
                      <li className="mb-2">
                        <Link to="/features/import" className="text-muted text-decoration-none">
                          <i className="bi bi-arrow-right me-2"></i>Import Bookmarks
                        </Link>
                      </li>
                      <li className="mb-2">
                        <Link to="/pocket-alternative" className="text-muted text-decoration-none">
                          <i className="bi bi-arrow-right me-2"></i>Pocket Alternative
                        </Link>
                      </li>
                      <li>
                        <Link to="/pricing" className="text-muted text-decoration-none">
                          <i className="bi bi-arrow-right me-2"></i>Pricing
                        </Link>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto text-center">
              <h2 className="h3 fw-bold mb-3">Ready to organize your bookmarks?</h2>
              <p className="text-muted mb-4">
                Simplify your digital life with onBookmarks.
              </p>
              <Link
                to={`${process.env.REACT_APP_APP_URL}/register`}
                className="btn btn-purple btn-lg px-5"
              >
                Get Started Free
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default BlogPage;
